import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import * as routes from "../data/links/routes";
import { useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect, lazy } from "react";
import Loader from "../components/Display/Loader";
import { errorTexts } from "../data/assets/texts";
import { getUsersWorkouts } from "../api/workouts";
import useScreenSize from "../context/ScreenContext";
import Challenge from "../components/Display/Challenge";
import useChallenges from "../context/ChallengeContext";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNotifications } from "../context/NotificationContext";
import ChallengeWorkout from "../components/Display/ChallengeWorkout";
import { challengeAthlete, getAthletesToChallenge } from "../api/user";
import {
  scrollBar,
  CommonTab,
  SpacedRow,
  MainText,
  FormButton,
} from "../data/style/Style";
import {
  getAthleteVersusStats,
  getChallengeStatistics,
} from "../api/statistics";

const TabList = lazy(() => import("@mui/lab/TabList"));
const TabPanel = lazy(() => import("@mui/lab/TabPanel"));
const TabContext = lazy(() => import("@mui/lab/TabContext"));

const Challenges = () => {
  const { user } = useAuth();
  const { challenges_data } = useChallenges();
  var {
    active_challenges_sent,
    active_challenges_received,
    pending_challenges_received,
    pending_challenges_sent,
  } = challenges_data;

  const { enableNotification } = useNotifications();
  const navigate = useNavigate();
  const { mobileDevice, size } = useScreenSize();
  var [width] = size;
  const [workouts, setWorkouts] = useState([]);
  const [limit, setLimit] = useState(0);
  const [workoutId, setWorkoutId] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [tabValue, setTabValue] = useState("1");

  useEffect(() => {
    if (hasMore)
      usersWorkoutsMutation.mutate({
        typeId: -1,
        categoryId: -1,
        levelId: user.level,
        limit: limit,
      });
  }, [hasMore, limit]);

  const athletesQuery = useQuery({
    queryKey: ["athletes", user.hasLevel, user.hasGender],
    queryFn: getAthletesToChallenge,
    staleTime: 12 * 60 * 60 * 1000,
  });

  const StatisticsQuery = useQuery({
    queryKey: ["challenge", "statistics", user.id],
    queryFn: getChallengeStatistics,
    staleTime: 12 * 60 * 60 * 1000,
  });

  if (StatisticsQuery.isFetched) {
    console.log(StatisticsQuery.data);
  }

  const usersWorkoutsMutation = useMutation({
    mutationFn: getUsersWorkouts,
    onSuccess: (response) => {
      if (response.data.workouts.length < 10) {
        setHasMore(false);
      }
      setWorkouts((prev) => prev.concat(response.data.workouts));
    },
    onError: (error) => {
      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }
      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }
      if (error.response.status >= 500) {
        enableNotification(errorTexts.system_error, "error");
      }
    },
  });

  const challengeAthleteMutation = useMutation({
    mutationFn: challengeAthlete,
    onSuccess: (response) => {
      console.log(response);
      if (response.status === 400) {
        enableNotification(response.data.message, "error");
        return;
      }
    },
    onError: (error) => console.log(error),
  });

  let challenge = (athleteId) => {
    if (!athleteId) return;

    if (user.active_challenges >= 5) {
      enableNotification("Challenges limit reached", "info");
      return;
    }

    if (workoutId === 0) {
      enableNotification("workout required", "error");
      return;
    }

    challengeAthleteMutation.mutate({
      athleteId,
      workoutId,
    });
  };

  const handleChange = (event, newValue) => setTabValue(newValue);

  return (
    <Stack
      height={"88%"}
      direction={"row"}
      justifyContent={"space-between"}
      flexWrap={"wrap"}
    >
      <Stack gap={1} width={"40%"} height={"88vh"}>
        <TabContext value={tabValue}>
          <TabList
            onChange={handleChange}
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#3DDC97",
              },
            }}
          >
            <CommonTab
              mobile={width < 750 || mobileDevice}
              label={
                "Active (" +
                (active_challenges_sent.length +
                  active_challenges_received.length) +
                ")"
              }
              value="1"
            />
            <CommonTab
              mobile={width < 750 || mobileDevice}
              label={
                "Pending (" +
                (pending_challenges_sent.length +
                  pending_challenges_received.length) +
                ")"
              }
              value="2"
            />
            <CommonTab
              mobile={width < 750 || mobileDevice}
              label="History"
              value="3"
            />
            <CommonTab
              mobile={width < 750 || mobileDevice}
              label="Statistics"
              value="4"
            />
          </TabList>
          <TabPanel value="1">
            <Stack gap={2}>
              <SpacedRow gap={1}>
                <MainText>Against</MainText>
                <Box width={"30%"}>
                  <MainText>Expires</MainText>
                </Box>
              </SpacedRow>
              <Stack height={"71vh"} overflow={"auto"} sx={scrollBar} pr={0.4}>
                {active_challenges_sent.map((item, index) => (
                  <Challenge
                    key={index}
                    image_url={item.challenged_image}
                    challenger={item.challenged}
                    expires={item.expire_timestamp}
                    challenge_id={item.id}
                    workout_id={item.workout}
                  />
                ))}
                {active_challenges_received.map((item, index) => (
                  <Challenge
                    key={index}
                    image_url={item.challenger_image}
                    challenger={item.challenger}
                    expires={item.expire_timestamp}
                    challenge_id={item.id}
                    workout_id={item.workout}
                  />
                ))}
              </Stack>
            </Stack>
          </TabPanel>
          <TabPanel value="2">
            <Stack gap={2}>
              <SpacedRow alignItems={"center"} gap={1}>
                <MainText>Against</MainText>
                <Box width={"35%"}>
                  <MainText>Invitation Expires</MainText>
                </Box>
              </SpacedRow>
              <Stack height={"71vh"} overflow={"auto"} sx={scrollBar} pr={0.4}>
                {pending_challenges_received.map((item, index) => (
                  <Challenge
                    key={index}
                    image_url={item.challenger_image}
                    challenger={item.challenger}
                    expires={item.expire_timestamp}
                    challenge_id={item.id}
                    workout_id={item.workout}
                    pending={true}
                  />
                ))}
                {pending_challenges_sent.map((item, index) => (
                  <Challenge
                    key={index}
                    image_url={item.challenged_image}
                    challenger={item.challenged}
                    expires={item.expire_timestamp}
                    challenge_id={item.id}
                    workout_id={item.workout}
                  />
                ))}
              </Stack>
            </Stack>
          </TabPanel>
          <TabPanel value={"3"}>
            <Stack gap={2} height={"71vh"} overflow={"auto"} sx={scrollBar}>
              <SpacedRow
                mt={2}
                alignItems={"center"}
                p={1}
                sx={{
                  background: "#0e1c27",
                  // border: "1px solid  #f54943",
                  borderRadius: "6px",
                }}
                gap={1}
              >
                <MainText sx={{ color: "#f54943 !important" }}>
                  25/03/2026
                </MainText>
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <MainText sx={{ color: "#f54943 !important" }}>
                    Ladder
                  </MainText>
                  <InfoIcon sx={{ color: "#E7E8E6" }} />
                </Stack>
                <Stack direction="row" gap={1} alignItems={"center"}>
                  <MainText sx={{ color: "#f54943 !important" }}>
                    Rxtest142
                  </MainText>
                  <Avatar />
                </Stack>
              </SpacedRow>
              <SpacedRow
                mt={2}
                alignItems={"center"}
                p={1}
                sx={{
                  background: "#0e1c27",
                  borderRadius: "6px",
                }}
                gap={1}
              >
                <MainText sx={{ color: " #59f061 !important" }}>
                  25/03/2026
                </MainText>
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <MainText sx={{ color: " #59f061 !important" }}>
                    Ladder
                  </MainText>
                  <InfoIcon sx={{ color: "#E7E8E6" }} />
                </Stack>
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <MainText sx={{ color: " #59f061 !important" }}>
                    Rxtest142
                  </MainText>
                  <Avatar />
                </Stack>
              </SpacedRow>
            </Stack>
          </TabPanel>
          <TabPanel value={"4"}>
            <Stack width={"30%"} gap={1} pl={2}>
              <MainText>Challenge statistics</MainText>
              <MainText>Total Challenges</MainText>
              <MainText>Sent Challenges</MainText>
              <MainText>Received Challenges</MainText>
              <MainText>Win Challenges</MainText>
              <MainText>Lost Challenges</MainText>
              <MainText>Draw Challenges</MainText>
              <MainText>Bigest rival (most challenges against)</MainText>
              <MainText>Most wins against</MainText>
              <MainText>Most loses against</MainText>
            </Stack>
          </TabPanel>
        </TabContext>
      </Stack>
      <Stack
        width={"25%"}
        pl={1}
        pr={1}
        gap={2}
        sx={scrollBar}
        overflow={"auto"}
        height={"88vh"}
      >
        {usersWorkoutsMutation.isSuccess ? (
          <>
            {workouts.map((item, index) => (
              <Box
                key={index}
                onClick={() => setWorkoutId(item)}
                sx={{ opacity: workoutId === item && "50%" }}
              >
                <ChallengeWorkout workoutId={item} />
              </Box>
            ))}
          </>
        ) : (
          <Loader height={"88vh"} minHeight={"500px"} />
        )}
      </Stack>
      <Stack width={"25%"} height={"88vh"} gap={2} pl={2}>
        <MainText>Athletes eligible to challenge</MainText>
        {athletesQuery.isFetched ? (
          <Stack
            height={"82vh"}
            overflow={"auto"}
            gap={1}
            sx={scrollBar}
            pr={0.4}
            pb={1}
          >
            {athletesQuery.data.map((item, index) => (
              <AthleteToChallenge
                key={index}
                athlete={item}
                challenge={challenge}
              />
            ))}
          </Stack>
        ) : (
          <Loader height={"80vh"} />
        )}
      </Stack>
    </Stack>
  );
};

export default Challenges;

const AthleteToChallenge = ({ athlete, challenge }) => {
  const VersusQuery = useQuery({
    queryKey: ["challenge", "stats", "versus", athlete.user],
    queryFn: () => getAthleteVersusStats(athlete.user),
    // staleTime: 12 * 60 * 60 * 1000,
  });

  return (
    <Stack
      direction={"row"}
      gap={2}
      p={2}
      sx={{ background: "black", borderRadius: "8px" }}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Stack alignItems={"center"} direction={"row"} gap={2}>
        <Avatar
          src={process.env.REACT_APP_BACKEND_DOMAIN + "/media/" + athlete.photo}
        />
        <Stack gap={0.5}>
          <MainText>{athlete.user_name}</MainText>
          {VersusQuery.isFetched && (
            <MainText>
              Wins: {VersusQuery.data.wins} Losses: {VersusQuery.data.losses}{" "}
              Draws:{VersusQuery.data.draws}
            </MainText>
          )}
        </Stack>
      </Stack>
      <FormButton onClick={() => challenge(athlete.user)}>Challenge</FormButton>
    </Stack>
  );
};
